import { useEffect, useRef, useState } from 'react';
import { idiomaAtual, numero, t } from '../i18n/idioma';
import type { NetStrike } from '../world/types';
import { BALDES, MedidorDano, type DpsSnapshot } from '../world/dps';

export type StrikeOuvir = (s: NetStrike, meu: boolean, emVoce: boolean, nome: string) => void;

interface Props {
  listenStrike: (fn: StrikeOuvir | null) => void;
  listenStrikeCap: (fn: (() => void) | null) => void;
}

const TRACE = 'rgba(255,255,255,.04)';
const LINHA = 'rgba(201,162,39,.18)';
const OURO = '#e6c452';

function umDecimal(n: number): string {
  return n.toLocaleString(idiomaAtual(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function mmss(s: number): string {
  const t = Math.max(0, Math.floor(s));
  const m = Math.floor(t / 60);
  const sec = t % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

function celula(n: number, vazio: boolean): string {
  return vazio ? '-' : numero(n);
}

function rotuloAlvo(nome: string): string {
  return nome.startsWith('manequim.') ? t(nome) : nome;
}

/** O gráfico de 2 min do medidor (a aba Combate da Análise de caçada também usa). */
export function Spark({ snap }: { snap: DpsSnapshot }) {
  const w = 262;
  const h = 28;
  const base = 25;
  const topo = 5;
  const pico = Math.max(...snap.baldes.map(b => b.dps), 0.001);
  const n = snap.baldes.length;
  const pts = snap.baldes.map((b, i) => {
    const x = n <= 1 ? 0 : (i / (n - 1)) * w;
    const y = base - (b.dps / pico) * (base - topo);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  const faixas: { x: number; width: number }[] = [];
  let runStart: number | null = null;
  const xDe = (i: number) => (n <= 1 ? 0 : (i / (n - 1)) * w);
  for (let i = 0; i < snap.baldes.length; i++) {
    if (snap.baldes[i].vazio) {
      if (runStart == null) runStart = xDe(i);
    } else if (runStart != null) {
      faixas.push({ x: runStart, width: Math.max(2, xDe(i) - runStart) });
      runStart = null;
    }
  }
  if (runStart != null) faixas.push({ x: runStart, width: Math.max(2, w - runStart) });

  return (
    <>
      <svg
        className="spark"
        viewBox={`0 0 ${w} ${h}`}
        width={w}
        height={h}
        role="img"
        aria-label={t('dps.spark')}
      >
        {faixas.map((f, i) => (
          <rect key={i} x={f.x} y={3} width={f.width} height={22} fill={TRACE} />
        ))}
        <line x1={0} y1={base} x2={w} y2={base} stroke={LINHA} strokeWidth={1} />
        <polyline
          fill="none"
          stroke={OURO}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          points={pts}
        />
      </svg>
      <p className="mini">{t('dps.sparkNota', { n: snap.foraDeLuta, total: BALDES })}</p>
    </>
  );
}

export function DpsGump({ listenStrike, listenStrikeCap }: Props) {
  const acc = useRef(new MedidorDano());
  const [snap, setSnap] = useState(() => acc.current.snapshot());

  useEffect(() => {
    listenStrike((s, meu, emVoce, nome) => {
      acc.current.aplicar(s, meu, emVoce, nome);
    });
    listenStrikeCap(() => acc.current.marcarTeto());
    const id = window.setInterval(() => setSnap(acc.current.snapshot()), 1000);
    return () => {
      listenStrike(null);
      listenStrikeCap(null);
      window.clearInterval(id);
    };
  }, [listenStrike, listenStrikeCap]);

  const zerar = () => {
    acc.current.zerar();
    setSnap(acc.current.snapshot());
  };

  const dpsHero = snap.dpsLuta == null ? '-' : umDecimal(snap.dpsLuta);
  const corrido = snap.dpsCorrido == null ? '-' : umDecimal(snap.dpsCorrido);
  const spark = snap.paredeS >= 5;
  const totalAlvo = snap.alvos.reduce((s, a) => s + a.dano, 0);

  return (
    <div className="dps">
      <div className="hero">
        <span className="hero__v">{dpsHero}</span>
        <span className="hero__u">{t('dps.porSegundoLuta')}</span>
        <span className="hero__t">{mmss(snap.paredeS)}</span>
      </div>
      <div className="sub">
        <span>{t('dps.corrido')} <b>{corrido}</b></span>
        {snap.lutaS > 0 && (
          <>
            <span>·</span>
            <span>{mmss(snap.lutaS)} {t('dps.emLuta')}</span>
          </>
        )}
      </div>

      {spark && <Spark snap={snap} />}

      <div className="risco" />
      <div className="par2">
        <span>{t('dps.dano')} <b>{celula(snap.dano, snap.vazio)}</b></span>
        <span>{t('dps.criticos')} <b>{celula(snap.criticos, snap.vazio)}</b></span>
        <span>{t('dps.maior')} <b>{celula(snap.maior, snap.vazio)}</b></span>
        <span>{t('dps.mortes')} <b>{celula(snap.mortes, snap.vazio)}</b></span>
      </div>

      {snap.alvos.length > 0 && (
        <>
          <div className="risco" />
          {snap.alvos.length > 1 && totalAlvo > 0 && (
            <div className="trilho">
              {snap.alvos.map((a, i) => (
                <i
                  key={a.nome}
                  style={{
                    width: `${(a.dano / totalAlvo) * 100}%`,
                    background: i === 0 ? 'var(--gold)' : 'var(--ink-dim)',
                    opacity: 0.85
                  }}
                />
              ))}
            </div>
          )}
          <table className="alvos">
            <thead>
              <tr>
                <th>{t('dps.alvo')}</th>
                <th>{t('dps.dano')}</th>
                <th>{t('dps.dps')}</th>
              </tr>
            </thead>
            <tbody>
              {snap.alvos.map(a => (
                <tr key={a.nome}>
                  <td>{rotuloAlvo(a.nome)}</td>
                  <td className="forte">{numero(a.dano)}</td>
                  <td>{a.dps == null ? '-' : umDecimal(a.dps)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {snap.sofrido > 0 && (
        <>
          <div className="risco" />
          <div className="par2">
            <span>{t('dps.sofrido')} <b className="sofrido">{numero(snap.sofrido)}</b></span>
            <span>{t('dps.porSegundoLuta')} <b>{snap.dpsSofrido == null ? '-' : umDecimal(snap.dpsSofrido)}</b></span>
          </div>
        </>
      )}

      <div className="rodape">
        <span className="rodape__i">
          {snap.quadrosNoTeto > 0 && t('dps.quadrosNoTeto', { n: snap.quadrosNoTeto })}
        </span>
        <button
          type="button"
          className="btn btn--tiny btn--primary"
          disabled={snap.vazio && snap.sofrido === 0 && snap.quadrosNoTeto === 0}
          onClick={zerar}
        >
          {t('dps.zerar')}
        </button>
      </div>
    </div>
  );
}
